/**
 * The pure model of the Spraywall hold editor (review of a detection run and
 * editing of the saved holds): the decoded items, the review state and its
 * reducer, the D79 tap table, the approve payload, the hand-drawn circle, the
 * smart-pick crop square and candidate choice, and the render culling.
 * Spec: the "Hold review and hold editing", "Kiválasztás — moving and point
 * editing", "Okos jelölés on the client" and "The resumable review and the
 * local review state" sections of docs/spec/21p-spraywall-hold-editor.md.
 *
 * Pure (no React, no Firebase, no DOM) so `SprayWallHoldEditor` stays a thin
 * view and every row of the D79 tap table is unit-tested here rather than
 * through pointer events. Client-only: no other runtime needs it. All the
 * geometry (`decodePoly`, `encodePoly`, `validateHoldPoly`, `polyArea`,
 * `polyOverlap`, `withoutExistingTwins`, and `holdAtPoint` for the hit test)
 * comes from `@sendly/shared` — a local copy would be a `noNewTwins` twin.
 *
 * Coordinates are normalized to the photo (0–1, origin top-left) unless a
 * parameter says photo px. Every function tolerates null / malformed input.
 *
 * The photo-swap review („Csak fotócsere", plan 8.5, D71) lives here too:
 * the run test, the review photo, the moved old holds, the centre-based
 * manual-alignment sliders and the re-match. Spec: "Reviewing the matching"
 * and "Manual alignment" in docs/spec/21f-spraywall-wall-refresh.md. The
 * transform and the matching are the shared `applyHoldTransform` /
 * `matchHolds`, the same code `approveSprayHolds` runs.
 */
import {
  decodePoly, encodePoly, validateHoldPoly, polyArea, polyOverlap, withoutExistingTwins, holdAtPoint,
  applyHoldTransform, matchHolds, isSprayPhotoPath, MIN_TRANSFORM_SCALE, MAX_TRANSFORM_SCALE, MAX_TRANSFORM_ROT,
  POLY_MAX_POINTS, isOutlinePoly, applyOutlineTransform, holdOutsideOutline,
} from "@sendly/shared";

// The smart-pick crop the server cuts around the tap (plan 8.3), in photo px.
const PICK_CROP_PX = 256;
// A hand-drawn circle smaller than this (photo px) is a slip, not a hold.
const MIN_CIRCLE_RADIUS_PX = 4;
// The circle uses every point a stored outline may have.
const CIRCLE_POINTS = POLY_MAX_POINTS;
// An outline needs a triangle at least (the stored format's lower bound).
const MIN_OUTLINE_POINTS = 3;
// Handles only on a hold at least this big on screen (CSS px, the shorter
// side of its box): on a smaller one they would cover the hold, and a drag
// meant to move it would grab a point instead. A coarse pointer (a finger) has
// bigger handles, so the hold must be bigger too for its inside to stay grabbable.
const HANDLE_MIN_SCREEN_PX = 56;
const HANDLE_MIN_SCREEN_PX_COARSE = 96;
// The grab radius of a point handle and of an edge circle (CSS px): mouse, and
// touch / pen (a fingertip covers far more than a mouse cursor does).
const VERTEX_HIT_SCREEN_PX = 10;
const EDGE_HIT_SCREEN_PX = 9;
const VERTEX_HIT_TOUCH_SCREEN_PX = 22;
const EDGE_HIT_TOUCH_SCREEN_PX = 20;
// An edge circle is shown only on an edge at least this long on screen, so it
// never sits on top of a point handle.
const EDGE_MIN_SCREEN_PX = 24;
const EDGE_MIN_SCREEN_PX_COARSE = 48;
// Near-twin threshold of a smart-pick candidate and an existing item (D79).
const PICK_TWIN_IOU = 0.7;
// Render culling keeps outlines this fraction of the view box outside it, so a pan does not pop them in late.
const VIEW_MARGIN_FRAC = 0.1;

const ADDED_SOURCES = new Set(["pick", "manual"]);

/** A Set of ids from a Set, an array or nothing. */
function asIdSet(ids) {
  if (ids instanceof Set) return ids;
  return new Set(Array.isArray(ids) ? ids : []);
}

/** Decoded outline points of a stored poly string, or null when it has no real outline. */
function outlinePoints(poly) {
  const points = decodePoly(poly);
  return Array.isArray(points) && points.length >= 3 ? points : null;
}

/** The version's holds with their decoded `points`; holds without a decodable outline are skipped. */
export function decodeHolds(holds) {
  if (!Array.isArray(holds)) return [];
  const out = [];
  for (const hold of holds) {
    if (!hold || typeof hold !== "object") continue;
    const points = outlinePoints(hold.poly);
    if (points) out.push({ ...hold, points });
  }
  return out;
}

/**
 * A run's proposals as `{ idx, poly, points, score, matchedHoldId }`; `idx` is
 * the index in the stored array (what `approveSprayHolds` expects). A proposal
 * without a storable outline is skipped — the server would refuse it anyway.
 */
export function decodeProposals(proposals) {
  if (!Array.isArray(proposals)) return [];
  const out = [];
  proposals.forEach((p, idx) => {
    if (!p || typeof p !== "object" || !validateHoldPoly(p.poly)) return;
    const points = outlinePoints(p.poly);
    if (!points) return;
    out.push({
      idx,
      poly: p.poly,
      points,
      score: Number.isFinite(p.score) ? p.score : 0,
      matchedHoldId: typeof p.matchedHoldId === "string" && p.matchedHoldId !== "" ? p.matchedHoldId : null,
      outside: p.outside === true,
    });
  });
  return out;
}

/**
 * The `keep` pre-fill of a refresh review (plan 8.5, D71). Same photo (A′):
 * every old hold no proposal matched — mostly the model's earlier misses, so
 * nothing silently goes missing. Photo swap: only the unmatched hand-made
 * holds (`src !== "auto"`), which the model never saw.
 */
export function keepPrefill(versionHolds, matchedIds, { photoSwap = false } = {}) {
  if (!Array.isArray(versionHolds)) return [];
  const matched = asIdSet(matchedIds);
  const out = [];
  for (const h of versionHolds) {
    if (!h || typeof h !== "object" || typeof h.id !== "string" || h.id === "") continue;
    if (matched.has(h.id)) continue;
    if (photoSwap && h.src === "auto") continue;
    out.push(h.id);
  }
  return out;
}

/**
 * A fresh identity old → new similarity `{ tx, ty, scale, rot }` (plan §5.3)
 * — what the server stores when it found no consensus, and the review's
 * fallback for a run transform that is missing or not finite. A function, not
 * a module constant: an `IDENTITY_TRANSFORM` constant here would be a
 * `noNewTwins` twin of the one in `apps/functions-vision/src/detect.js` (and
 * of the unexported one in `@sendly/shared`); moving it into the package is
 * requested from the integrator (step-7 requests-C1.md).
 */
function identityTransform() {
  return { tx: 0, ty: 0, scale: 1, rot: 0 };
}

/** A copy of the transform when its four values are finite, else null. */
function finiteTransform(t) {
  if (!t || typeof t !== "object" || ![t.tx, t.ty, t.scale, t.rot].every(Number.isFinite)) return null;
  return { tx: t.tx, ty: t.ty, scale: t.scale, rot: t.rot };
}

/**
 * The transform clamped to the bounds `approveSprayHolds` checks (scale and
 * |rot|), so a re-matched payload can never be refused as `invalidTransform`.
 * Not finite → identity.
 */
function clampTransform(t) {
  const f = finiteTransform(t);
  if (!f) return identityTransform();
  return {
    tx: f.tx,
    ty: f.ty,
    scale: Math.min(MAX_TRANSFORM_SCALE, Math.max(MIN_TRANSFORM_SCALE, f.scale)),
    rot: Math.min(MAX_TRANSFORM_ROT, Math.max(-MAX_TRANSFORM_ROT, f.rot)),
  };
}

/** The version's holds keyed by id (string ids only). */
function holdsById(versionHolds) {
  const out = new Map();
  for (const h of Array.isArray(versionHolds) ? versionHolds : []) {
    if (h && typeof h === "object" && typeof h.id === "string") out.set(h.id, h);
  }
  return out;
}

/**
 * The photo-swap part of a review state for a given pairing: the unmatched
 * old holds moved onto the pending photo (display only — the server moves
 * them again on approval with the final transform), and the D71 `keep`
 * pre-fill, also remembered as `prefilled` for the highlight. Unusable photo
 * sizes (the shared transform throws on them) leave the outlines where they
 * were instead of failing the whole review.
 */
function photoSwapOldPart(versionHolds, matchedIds, transform, fromSize, toSize) {
  const unmatched = (Array.isArray(versionHolds) ? versionHolds : [])
    .filter((h) => h && typeof h === "object" && typeof h.id === "string" && !matchedIds.has(h.id));
  let moved = unmatched;
  try {
    moved = applyHoldTransform(unmatched, transform, fromSize, toSize);
  } catch {
    moved = unmatched;
  }
  const keep = new Set(keepPrefill(versionHolds, matchedIds, { photoSwap: true }));
  return { oldHolds: decodeHolds(moved), keep, prefilled: new Set(keep) };
}

/**
 * Is this run a photo swap ("Csak fotócsere")? Exactly the server's test in
 * `approveSprayHolds`: a refresh run whose `photoPath` is a `pending_` file of
 * this wall. A same-photo refresh (D47) runs on the version's own file, so
 * the path alone tells the two apart — no extra flag on the run.
 */
export function isPhotoSwapRun(run, { gymId, wallId } = {}) {
  if (!run || typeof run !== "object" || run.mode !== "refresh") return false;
  return isSprayPhotoPath(gymId, wallId, run.photoPath, { prefix: "pending_" });
}

/**
 * The photo a review is drawn on, `{ url, width, height }` (0 = unknown size,
 * as the hold editor expects). A photo swap is reviewed on the PENDING photo
 * the run saw, because its proposals are in that photo's space; `url` may be
 * null when Storage gave none (such a run cannot be reviewed). Otherwise the
 * version's photo, its size falling back to the run's decoded size — a
 * version written before the fresh-approve size fix may carry none.
 */
export function reviewPhotoOf({ version, run, photoSwap = false } = {}) {
  const r = run && typeof run === "object" ? run : {};
  const positive = (v) => (Number.isFinite(v) && v > 0 ? v : 0);
  const urlOf = (v) => (typeof v === "string" && v !== "" ? v : null);
  if (photoSwap === true) {
    return { url: urlOf(r.photoUrl), width: positive(r.photoWidth), height: positive(r.photoHeight) };
  }
  const photo = version && typeof version === "object" && version.photo && typeof version.photo === "object" ? version.photo : {};
  return {
    url: urlOf(photo.url),
    width: positive(photo.width) || positive(r.photoWidth),
    height: positive(photo.height) || positive(r.photoHeight),
  };
}

/**
 * The starting review state of a detection run. A matched proposal starts
 * with the old hold's feet-only value, a new one with `false` (there is no
 * machine feet-only suggestion, D76). Refresh mode also lists the unmatched
 * old holds and pre-fills `keep` with them.
 *
 * Photo swap (`photoSwap: true`, refresh only): the unmatched old holds are
 * moved onto the pending photo with the run's `transform` (identity when it
 * is not finite) from `fromSize` (old photo px) to `toSize` (new photo px),
 * and `keep` gets the D71 pre-fill — only the hand-made holds the model never
 * saw — which is also stored as `prefilled` for the highlight and the
 * „N kézzel jelölt fogás…" hint. `manualMatches` turns true only after a
 * manual re-match (`realignReview`), the one case the payload carries
 * `matches` + `transform`.
 *
 * The wall outline (21p "Fal kontúr — the wall outline"): `outline` is the
 * run's proposed outline (none = null); a proposal the run flagged
 * `outside` starts rejected (D4). `outlineEdited` turns true on the
 * manager's first outline change.
 */
export function initReviewState({ mode, proposals, versionHolds, photoSwap = false, transform = null, fromSize = null, toSize = null, outline = null } = {}) {
  const reviewMode = mode === "refresh" ? "refresh" : "fresh";
  const swap = reviewMode === "refresh" && photoSwap === true;
  const decoded = decodeProposals(proposals);
  const holdById = holdsById(versionHolds);
  const items = decoded.map((p) => {
    const old = p.matchedHoldId ? holdById.get(p.matchedHoldId) : null;
    return { ...p, accepted: p.outside !== true, feetOnly: reviewMode === "refresh" && old ? old.feetOnly === true : false };
  });
  let oldHolds = [];
  let keep = new Set();
  let prefilled = new Set();
  let reviewTransform = null;
  if (reviewMode === "refresh") {
    const matchedIds = new Set(items.map((p) => p.matchedHoldId).filter(Boolean));
    if (swap) {
      reviewTransform = finiteTransform(transform) || identityTransform();
      ({ oldHolds, keep, prefilled } = photoSwapOldPart(versionHolds, matchedIds, reviewTransform, fromSize, toSize));
    } else {
      oldHolds = decodeHolds(versionHolds).filter((h) => typeof h.id === "string" && !matchedIds.has(h.id));
      keep = new Set(keepPrefill(versionHolds, matchedIds));
    }
  }
  return {
    mode: reviewMode, proposals: items, oldHolds, keep, added: [], selected: null, nextTempId: 1,
    prefilled, photoSwap: swap, transform: reviewTransform, manualMatches: false,
    outline: isOutlinePoly(outline) ? outline : null, outlineEdited: false,
  };
}

/** A slider's centre point of a photo (px); an unusable size pins it to the corner, so the conversion stays an exact inverse. */
function photoCentre(size) {
  const w = size && typeof size === "object" ? size.width : null;
  const h = size && typeof size === "object" ? size.height : null;
  return [Number.isFinite(w) && w > 0 ? w / 2 : 0, Number.isFinite(h) && h > 0 ? h / 2 : 0];
}

/**
 * The manual-alignment sliders of a transform. The server transform turns
 * about the old photo's top-left corner (`p' = s·R(rot)·p + (tx, ty)`); a
 * slider turning about a corner would swing the whole overlay off the wall,
 * so the sliders work about the photo centres: `shift = (tx, ty) + s·R·c_old
 * − c_new`. Not finite → the sliders of the identity transform.
 */
export function slidersFromTransform(transform, fromSize, toSize) {
  const t = finiteTransform(transform) || identityTransform();
  const [ox, oy] = photoCentre(fromSize);
  const [nx, ny] = photoCentre(toSize);
  const ca = t.scale * Math.cos(t.rot);
  const sa = t.scale * Math.sin(t.rot);
  return {
    shiftX: t.tx + (ca * ox - sa * oy) - nx,
    shiftY: t.ty + (sa * ox + ca * oy) - ny,
    scale: t.scale,
    rotDeg: (t.rot * 180) / Math.PI,
  };
}

/**
 * The server transform of the sliders — the exact inverse of
 * `slidersFromTransform`: `(tx, ty) = c_new + shift − s·R(rot)·c_old`.
 * Non-finite slider values count as their identity value.
 */
export function transformFromSliders(sliders, fromSize, toSize) {
  const s = sliders && typeof sliders === "object" ? sliders : {};
  const shiftX = Number.isFinite(s.shiftX) ? s.shiftX : 0;
  const shiftY = Number.isFinite(s.shiftY) ? s.shiftY : 0;
  const scale = Number.isFinite(s.scale) ? s.scale : 1;
  const rot = Number.isFinite(s.rotDeg) ? (s.rotDeg * Math.PI) / 180 : 0;
  const [ox, oy] = photoCentre(fromSize);
  const [nx, ny] = photoCentre(toSize);
  const ca = scale * Math.cos(rot);
  const sa = scale * Math.sin(rot);
  return { tx: nx + shiftX - (ca * ox - sa * oy), ty: ny + shiftY - (sa * ox + ca * oy), scale, rot };
}

/**
 * The slider ranges: the shift spans half the new photo each way (W, H =
 * `toSize`), zoom and rotation use the very bounds `approveSprayHolds`
 * checks, so a slider can never produce a transform the approval refuses.
 */
export function sliderBounds(toSize) {
  const size = toSize && typeof toSize === "object" ? toSize : {};
  const w = Number.isFinite(size.width) && size.width > 0 ? size.width : 0;
  const h = Number.isFinite(size.height) && size.height > 0 ? size.height : 0;
  const maxDeg = (MAX_TRANSFORM_ROT * 180) / Math.PI;
  return {
    shiftX: { min: -w / 2, max: w / 2, step: 1 },
    shiftY: { min: -h / 2, max: h / 2, step: 1 },
    scale: { min: MIN_TRANSFORM_SCALE, max: MAX_TRANSFORM_SCALE, step: 0.005 },
    rotDeg: { min: -maxDeg, max: maxDeg, step: 0.1 },
  };
}

/** The sliders clamped to `sliderBounds(toSize)`; a non-finite value becomes its identity value (shift 0, zoom 1, rotation 0). */
export function clampSliders(sliders, toSize) {
  const s = sliders && typeof sliders === "object" ? sliders : {};
  const b = sliderBounds(toSize);
  const clamp = (v, range, identity) => (Number.isFinite(v) ? Math.min(range.max, Math.max(range.min, v)) : identity);
  return {
    shiftX: clamp(s.shiftX, b.shiftX, 0),
    shiftY: clamp(s.shiftY, b.shiftY, 0),
    scale: clamp(s.scale, b.scale, 1),
    rotDeg: clamp(s.rotDeg, b.rotDeg, 0),
  };
}

/**
 * „Újrapárosítás" after a manual alignment (photo swap only; any other state
 * comes back as is). The shared `matchHolds` pairs again at the clamped
 * transform; the admin's decisions survive where they still apply: every
 * proposal keeps `accepted`, its `feetOnly` is reset only when its pairing
 * changed (to the new old hold's value, `false` when now unmatched), the
 * added items stay. The old holds, `keep` and `prefilled` are rebuilt from
 * the new pairing (D71 again) and the selection is cleared, because the
 * selected outline may no longer exist. `manualMatches: true` makes the
 * payload carry `matches` + `transform`.
 *
 * Only the proposals the review kept take part: a proposal `decodeProposals`
 * skipped can never become a hold, so it must not take an old hold away
 * from a real one (its slot is passed as null, which `matchHolds` ignores).
 * Unusable photo sizes leave the state unchanged.
 */
export function realignReview(state, { transform, versionHolds, proposals, fromSize, toSize, versionOutline = null } = {}) {
  if (!state || typeof state !== "object" || state.photoSwap !== true || !Array.isArray(state.proposals)) return state;
  const t = clampTransform(transform);
  const runProposals = Array.isArray(proposals) ? proposals : [];
  const slots = [];
  for (const p of state.proposals) {
    const fromRun = runProposals[p.idx];
    slots[p.idx] = fromRun && typeof fromRun === "object" && typeof fromRun.poly === "string" ? fromRun : { poly: p.poly };
  }
  for (let i = 0; i < slots.length; i++) {
    if (slots[i] === undefined) slots[i] = null;
  }
  let result;
  try {
    result = matchHolds(versionHolds, slots, t, fromSize, toSize);
  } catch {
    return state;
  }
  const pairByIdx = new Map(result.matches.map((m) => [m.proposalIdx, m.holdId]));
  const holdById = holdsById(versionHolds);
  const items = state.proposals.map((p) => {
    const next = pairByIdx.get(p.idx) ?? null;
    if (next === p.matchedHoldId) return p;
    const old = next ? holdById.get(next) : null;
    return { ...p, matchedHoldId: next, feetOnly: old ? old.feetOnly === true : false };
  });
  const matchedIds = new Set(items.map((p) => p.matchedHoldId).filter(Boolean));
  const { oldHolds, keep, prefilled } = photoSwapOldPart(versionHolds, matchedIds, t, fromSize, toSize);
  // The wall outline moves with the new transform too, unless the manager already edited it here.
  const moved = state.outlineEdited === true ? null : applyOutlineTransform(versionOutline, t, fromSize, toSize);
  const outline = moved || (state.outline === undefined ? null : state.outline);
  return { ...state, proposals: items, oldHolds, keep, prefilled, selected: null, transform: t, manualMatches: true, outline };
}

/**
 * The photo-swap counter row: `matched` = accepted proposals that took over
 * an old hold, `newHolds` = accepted unmatched proposals plus the added
 * items, `dropped` = old holds not in `keep` („levéve?"), `keptManual` = the
 * D71 pre-marked holds still in `keep` (the hint's N — it drops as the admin
 * removes them).
 */
export function photoSwapCounts(state) {
  const out = { matched: 0, newHolds: 0, dropped: 0, keptManual: 0 };
  if (!state || typeof state !== "object") return out;
  const keep = asIdSet(state.keep);
  for (const p of Array.isArray(state.proposals) ? state.proposals : []) {
    if (!p || !p.accepted) continue;
    if (p.matchedHoldId) out.matched += 1;
    else out.newHolds += 1;
  }
  out.newHolds += Array.isArray(state.added) ? state.added.length : 0;
  for (const h of Array.isArray(state.oldHolds) ? state.oldHolds : []) {
    if (h && !keep.has(h.id)) out.dropped += 1;
  }
  for (const id of asIdSet(state.prefilled)) {
    if (keep.has(id)) out.keptManual += 1;
  }
  return out;
}

/**
 * Every tappable / drawable item of the review, in one list for
 * `holdAtPoint` and the SVG layer: proposals, then the unmatched old holds,
 * then the added items. `doubtfulOnly` (the „Kétes" filter) keeps only the
 * proposals scored below `doubtfulScore`; without a numeric threshold the
 * filter does nothing.
 */
export function reviewTargets(state, { doubtfulOnly = false, doubtfulScore = null } = {}) {
  if (!state || typeof state !== "object") return [];
  const filter = doubtfulOnly === true && Number.isFinite(doubtfulScore);
  const out = [];
  for (const p of Array.isArray(state.proposals) ? state.proposals : []) {
    if (filter && !(p.score < doubtfulScore)) continue;
    out.push({
      kind: "proposal", id: p.idx, points: p.points, poly: p.poly, status: p.accepted ? "accepted" : "rejected",
      feetOnly: p.feetOnly === true, score: p.score, src: "auto", matchedHoldId: p.matchedHoldId, edited: p.edited === true,
    });
  }
  const keep = asIdSet(state.keep);
  // `prefilled` marks the D71 pre-kept hand holds of a photo swap, drawn highlighted.
  const prefilled = asIdSet(state.prefilled);
  for (const h of Array.isArray(state.oldHolds) ? state.oldHolds : []) {
    out.push({
      kind: "old", id: h.id, points: h.points, poly: h.poly, status: keep.has(h.id) ? "kept" : "dropped",
      feetOnly: h.feetOnly === true, score: null, src: h.src, note: typeof h.note === "string" ? h.note : null, missing: h.missing === true,
      prefilled: prefilled.has(h.id),
    });
  }
  for (const a of Array.isArray(state.added) ? state.added : []) {
    out.push({ kind: "added", id: a.tempId, points: a.points, poly: a.poly, status: "added", feetOnly: a.feetOnly === true, score: null, src: a.src });
  }
  return out;
}

/**
 * What a tap does (D79, plan 8.4 table). `tool` ∈ select / smartPick /
 * manualCircle; `target` = the `holdAtPoint` hit or null; `selected` =
 * `{ kind, id }` or null. Empty spot: select clears the selection, smartPick
 * runs a pick, manualCircle does nothing (a circle needs a drag). Any
 * unselected outline is selected by every tool. The selected outline again:
 * smartPick searches inside it, the other tools clear the selection — on a
 * densely covered photo there may be no empty spot left to tap.
 *
 * A `missing` hold is a target only in edit mode (`mode: "edit"`), where it
 * can be selected for „Visszaállítás"; in review mode it counts as an empty
 * spot. Selected again, it is deselected by every tool: a "search inside" a
 * ghost outline has nothing to find that a plain tap beside it would not.
 */
export function decideTap({ tool, target, selected, mode } = {}) {
  if (tool !== "select" && tool !== "smartPick" && tool !== "manualCircle") return { type: "none" };
  const hit = target && typeof target === "object" && !(target.missing === true && mode !== "edit") ? target : null;
  if (!hit) {
    if (tool === "select") return { type: "deselect" };
    if (tool === "smartPick") return { type: "pick", searchInside: false };
    return { type: "none" };
  }
  const isSelected = !!selected && selected.kind === hit.kind && selected.id === hit.id;
  if (!isSelected) return { type: "select", target: hit };
  if (tool === "smartPick" && hit.missing !== true) return { type: "pick", searchInside: true };
  return { type: "deselect" };
}

/**
 * The review state transitions. Unknown actions and invalid payloads return
 * the same state object. `addHold` also selects the new item, so „Következő
 * jelölt" and the action row act on it at once.
 */
export function reviewReducer(state, action) {
  if (!state || typeof state !== "object" || !action || typeof action !== "object") return state;
  switch (action.type) {
    case "select": {
      const t = action.target;
      if (!t || typeof t !== "object" || t.id === undefined || t.id === null) return state;
      return { ...state, selected: { kind: t.kind, id: t.id } };
    }
    case "deselect":
      return state.selected === null ? state : { ...state, selected: null };
    case "toggleReject": {
      if (!state.proposals.some((p) => p.idx === action.idx)) return state;
      return { ...state, proposals: state.proposals.map((p) => (p.idx === action.idx ? { ...p, accepted: !p.accepted } : p)) };
    }
    case "toggleFeetOnly": {
      // An old kept hold keeps its stored value; it is changed in edit mode, after the approval.
      if (action.kind === "proposal") {
        if (!state.proposals.some((p) => p.idx === action.id)) return state;
        return { ...state, proposals: state.proposals.map((p) => (p.idx === action.id ? { ...p, feetOnly: !p.feetOnly } : p)) };
      }
      if (action.kind === "added") {
        if (!state.added.some((a) => a.tempId === action.id)) return state;
        return { ...state, added: state.added.map((a) => (a.tempId === action.id ? { ...a, feetOnly: !a.feetOnly } : a)) };
      }
      return state;
    }
    case "toggleKeep": {
      if (!state.oldHolds.some((h) => h.id === action.id)) return state;
      const keep = new Set(state.keep);
      if (keep.has(action.id)) keep.delete(action.id);
      else keep.add(action.id);
      return { ...state, keep };
    }
    case "addHold": {
      if (!ADDED_SOURCES.has(action.src) || !validateHoldPoly(action.poly)) return state;
      const points = Array.isArray(action.points) ? action.points : outlinePoints(action.poly);
      if (!points) return state;
      const tempId = state.nextTempId;
      const item = { tempId, points, poly: action.poly, src: action.src, feetOnly: false };
      return { ...state, added: [...state.added, item], selected: { kind: "added", id: tempId }, nextTempId: tempId + 1 };
    }
    case "replaceAdded": {
      if (!validateHoldPoly(action.poly) || !state.added.some((a) => a.tempId === action.tempId)) return state;
      const points = Array.isArray(action.points) ? action.points : outlinePoints(action.poly);
      if (!points) return state;
      return { ...state, added: state.added.map((a) => (a.tempId === action.tempId ? { ...a, points, poly: action.poly } : a)) };
    }
    case "removeAdded": {
      if (!state.added.some((a) => a.tempId === action.tempId)) return state;
      const wasSelected = state.selected && state.selected.kind === "added" && state.selected.id === action.tempId;
      return { ...state, added: state.added.filter((a) => a.tempId !== action.tempId), selected: wasSelected ? null : state.selected };
    }
    case "setPoly": {
      // A moved / reshaped outline. A proposal remembers it was corrected by
      // hand („kézzel javítva"): the approval then sends its new outline.
      if (!validateHoldPoly(action.poly)) return state;
      const points = outlinePoints(action.poly);
      if (!points) return state;
      if (action.kind === "proposal") {
        if (!state.proposals.some((p) => p.idx === action.id)) return state;
        return { ...state, proposals: state.proposals.map((p) => (p.idx === action.id ? { ...p, poly: action.poly, points, edited: true } : p)) };
      }
      if (action.kind === "added") {
        if (!state.added.some((a) => a.tempId === action.id)) return state;
        return { ...state, added: state.added.map((a) => (a.tempId === action.id ? { ...a, poly: action.poly, points } : a)) };
      }
      return state;
    }
    case "rejectOutside": {
      // One step for every review item outside the CURRENT outline (21p "Fal kontúr").
      const out = outsideTargets(state, state.outline);
      if (out.count === 0) return state;
      const props = new Set(out.proposals);
      const added = new Set(out.added);
      const keep = new Set(state.keep);
      for (const id of out.old) keep.delete(id);
      const selectedGone = state.selected && (
        (state.selected.kind === "added" && added.has(state.selected.id))
      );
      return {
        ...state,
        proposals: state.proposals.map((p) => (props.has(p.idx) ? { ...p, accepted: false } : p)),
        added: state.added.filter((a) => !added.has(a.tempId)),
        keep,
        selected: selectedGone ? null : state.selected,
      };
    }
    case "setOutline": {
      // The wall outline: a storable polygon, or null = none (the whole photo).
      if (action.outline !== null && !isOutlinePoly(action.outline)) return state;
      if (action.outline === state.outline) return state;
      return { ...state, outline: action.outline, outlineEdited: true };
    }
    default:
      return state;
  }
}

/**
 * The review counters. `total` is what the version will hold after the
 * approval (accepted proposals + added items + kept old holds) — the number
 * the 1500 ceiling applies to; `feetOnly` counts the feet-only ones among them.
 */
export function reviewCounts(state) {
  const empty = { total: 0, feetOnly: 0, accepted: 0, rejected: 0, added: 0, kept: 0, dropped: 0 };
  if (!state || typeof state !== "object") return empty;
  const proposals = Array.isArray(state.proposals) ? state.proposals : [];
  const added = Array.isArray(state.added) ? state.added : [];
  const oldHolds = Array.isArray(state.oldHolds) ? state.oldHolds : [];
  const keep = asIdSet(state.keep);
  const acceptedList = proposals.filter((p) => p.accepted);
  const keptOld = oldHolds.filter((h) => keep.has(h.id));
  const feetOnly = acceptedList.filter((p) => p.feetOnly === true).length
    + added.filter((a) => a.feetOnly === true).length
    + keptOld.filter((h) => h.feetOnly === true).length;
  return {
    total: acceptedList.length + added.length + keep.size,
    feetOnly,
    accepted: acceptedList.length,
    rejected: proposals.length - acceptedList.length,
    added: added.length,
    kept: keep.size,
    dropped: oldHolds.length - keptOld.length,
  };
}

/**
 * The `approveSprayHolds` payload. `keep` only in refresh mode (the server
 * refuses it in fresh mode), in the old holds' order; `dryRun` only when
 * true. `accepted[].poly` only for a hand-corrected proposal („kézzel
 * javítva"). Never `added[].holdId` (no review gesture produces it). `matches` +
 * `transform` only after a manual re-match of a photo swap
 * (`manualMatches`): otherwise the server pairs by the run's own
 * `proposals[].matchedHoldId` and moves by the run's `transform`. `matches`
 * lists every paired proposal, accepted or not — the server counts only the
 * matched-and-accepted ones as claims.
 */
export function buildApprovePayload(state, { gymId, wallId, versionId, runId, dryRun = false } = {}) {
  const s = state && typeof state === "object" ? state : {};
  const proposals = Array.isArray(s.proposals) ? s.proposals : [];
  const added = Array.isArray(s.added) ? s.added : [];
  const payload = {
    gymId,
    wallId,
    versionId,
    runId,
    // `poly` only for a hand-corrected proposal: the server takes the run's own outline otherwise.
    accepted: proposals.filter((p) => p.accepted).map((p) => (
      p.edited === true && validateHoldPoly(p.poly)
        ? { proposalIdx: p.idx, feetOnly: p.feetOnly === true, poly: p.poly }
        : { proposalIdx: p.idx, feetOnly: p.feetOnly === true }
    )),
    added: added.map((a) => ({ poly: a.poly, src: a.src, feetOnly: a.feetOnly === true })),
  };
  if (s.mode === "refresh") {
    const keep = asIdSet(s.keep);
    const ordered = (Array.isArray(s.oldHolds) ? s.oldHolds : []).map((h) => h.id).filter((id) => keep.has(id));
    const listed = new Set(ordered);
    payload.keep = [...ordered, ...[...keep].filter((id) => !listed.has(id))];
    if (s.photoSwap === true && s.manualMatches === true) {
      payload.matches = proposals.filter((p) => p.matchedHoldId).map((p) => ({ proposalIdx: p.idx, holdId: p.matchedHoldId }));
      payload.transform = clampTransform(s.transform);
    }
  }
  // Always sent: the review's outline, or null when there is none (the server would otherwise take the run's).
  payload.outline = isOutlinePoly(s.outline) ? s.outline : null;
  if (dryRun === true) payload.dryRun = true;
  return payload;
}

/**
 * The hand-drawn circle (Kézi kör) as a stored outline: centre and radius in
 * photo px, `n` points (3–16), clamped to the photo and normalized. `null`
 * for a radius under 4 px, bad input, or an outline that degenerates (e.g. a
 * circle drawn entirely outside the photo).
 */
export function circleToPoly(cx, cy, r, width, height, n = CIRCLE_POINTS) {
  if (![cx, cy, r, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
  if (r < MIN_CIRCLE_RADIUS_PX) return null;
  const count = Number.isFinite(n) ? Math.min(CIRCLE_POINTS, Math.max(3, Math.round(n))) : CIRCLE_POINTS;
  const clip = (v) => Math.min(1, Math.max(0, v));
  const points = [];
  for (let k = 0; k < count; k++) {
    const angle = (2 * Math.PI * k) / count;
    points.push([clip((cx + r * Math.cos(angle)) / width), clip((cy + r * Math.sin(angle)) / height)]);
  }
  const poly = encodePoly(points);
  if (!validateHoldPoly(poly)) return null;
  return polyArea(decodePoly(poly)) > 0 ? poly : null;
}

/**
 * The dashed square shown while a smart pick runs: the 256 × 256 px crop the
 * server cuts around the tap (`x`, `y` normalized), shifted inward at the
 * photo edges; a photo smaller than the square shows the whole photo.
 * Photo px `{ x0, y0, w, h }`, or null on bad input.
 */
export function pickCropRect(x, y, width, height, size = PICK_CROP_PX) {
  if (![x, y, width, height, size].every(Number.isFinite) || width <= 0 || height <= 0 || size <= 0) return null;
  const half = size / 2;
  const x0 = Math.min(Math.max(Math.round(x * width - half), 0), Math.max(width - size, 0));
  const y0 = Math.min(Math.max(Math.round(y * height - half), 0), Math.max(height - size, 0));
  return { x0, y0, w: Math.min(size, width), h: Math.min(size, height) };
}

/**
 * Which smart-pick candidate becomes a hold (plan 8.3, D79). The server's
 * candidates (best first) lose every near-twin (IoU ≥ 0.7) of an existing
 * item; the first survivor is added, the rest wait for „Következő jelölt".
 * Nothing survives: on an empty-spot tap the existing item the first
 * candidate duplicates is selected instead („Ez már fogás."); a "search
 * inside" tap found nothing smaller (`noSmaller`). No candidate → `noHold`.
 */
export function choosePickCandidate({ candidates, existing, searchInside = false } = {}) {
  const decoded = [];
  for (const c of Array.isArray(candidates) ? candidates : []) {
    if (!c || typeof c !== "object" || !validateHoldPoly(c.poly)) continue;
    const points = outlinePoints(c.poly);
    if (points) decoded.push({ poly: points, candidate: { poly: c.poly, points, score: Number.isFinite(c.score) ? c.score : 0 } });
  }
  if (decoded.length === 0) return { outcome: "none", reason: "noHold" };

  const known = (Array.isArray(existing) ? existing : [])
    .filter((it) => it && typeof it === "object" && Array.isArray(it.points) && it.points.length >= 3)
    .map((it) => ({ poly: it.points, item: it }));
  const survivors = withoutExistingTwins(decoded, known, PICK_TWIN_IOU);
  if (survivors.length > 0) {
    return { outcome: "add", candidate: survivors[0].candidate, rest: survivors.slice(1).map((s) => s.candidate) };
  }
  if (searchInside === true) return { outcome: "none", reason: "noSmaller" };

  let target = null;
  let bestIoU = PICK_TWIN_IOU;
  for (const k of known) {
    const iou = polyOverlap(decoded[0].poly, k.poly);
    if (iou >= bestIoU && (target === null || iou > bestIoU)) {
      target = k.item;
      bestIoU = iou;
    }
  }
  return target ? { outcome: "existing", target } : { outcome: "none", reason: "noHold" };
}

/**
 * Render culling (plan 9.3): the items whose outline bounding box, in photo
 * px, intersects the view box `{ x, y, w, h }` grown by a margin. Without a
 * usable view box every item with an outline is returned.
 */
export function holdsInView(items, viewBox, width, height, marginFrac = VIEW_MARGIN_FRAC) {
  if (!Array.isArray(items)) return [];
  const withOutline = items.filter((it) => it && Array.isArray(it.points) && it.points.length > 0);
  const vb = viewBox && typeof viewBox === "object" ? viewBox : null;
  if (!vb || ![vb.x, vb.y, vb.w, vb.h, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return withOutline;
  const m = Number.isFinite(marginFrac) && marginFrac > 0 ? marginFrac : 0;
  const left = vb.x - vb.w * m;
  const right = vb.x + vb.w * (1 + m);
  const top = vb.y - vb.h * m;
  const bottom = vb.y + vb.h * (1 + m);
  return withOutline.filter((it) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const p of it.points) {
      const px = p[0] * width;
      const py = p[1] * height;
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }
    return maxX >= left && minX <= right && maxY >= top && minY <= bottom;
  });
}

// ── Kiválasztás — moving and point editing (21p) ──
// The outline functions below work in PHOTO PX (`[[x, y], …]`), because the
// limits are in px (the photo bounds, the 2 / 20 px nudge, the handle radii);
// `pointsToPx` / `pxToPoly` convert at the edges. `size` = `{ width, height }`.

const isPxPoint = (p) => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]);
const isPxOutline = (pts) => Array.isArray(pts) && pts.length >= MIN_OUTLINE_POINTS && pts.every(isPxPoint);
const usableSize = (size) => !!size && Number.isFinite(size.width) && Number.isFinite(size.height) && size.width > 0 && size.height > 0;

/** Normalized outline points → photo px; `[]` on bad input. */
export function pointsToPx(points, width, height) {
  if (!Array.isArray(points) || !(width > 0) || !(height > 0)) return [];
  return points.filter(isPxPoint).map((p) => [p[0] * width, p[1] * height]);
}

/**
 * A px outline → the stored polygon string (normalized, clamped to the photo),
 * or null when it is not storable (fewer than 3 or more than 16 points, zero
 * area, unusable photo size).
 */
export function pxToPoly(pxPoints, width, height) {
  if (!isPxOutline(pxPoints) || !(width > 0) || !(height > 0)) return null;
  const clip = (v) => Math.min(1, Math.max(0, v));
  const poly = encodePoly(pxPoints.map((p) => [clip(p[0] / width), clip(p[1] / height)]));
  if (!validateHoldPoly(poly)) return null;
  return polyArea(decodePoly(poly)) > 0 ? poly : null;
}

/** The outline's box in px: `{ x0, y0, x1, y1 }`. */
function outlineBox(pts) {
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/**
 * Are the point handles and edge circles drawn? Only when the hold is at
 * least 56 CSS px on screen (96 with a coarse pointer) — the shorter side of
 * its box, `pxPerScreenPx` being the photo px per CSS px at the current zoom.
 */
export function handlesVisible(polyPx, pxPerScreenPx, coarse = false) {
  if (!isPxOutline(polyPx) || !Number.isFinite(pxPerScreenPx) || pxPerScreenPx <= 0) return false;
  const b = outlineBox(polyPx);
  const min = coarse === true ? HANDLE_MIN_SCREEN_PX_COARSE : HANDLE_MIN_SCREEN_PX;
  return Math.min(b.x1 - b.x0, b.y1 - b.y0) / pxPerScreenPx >= min;
}

/** The edge circles: the middle of every edge, edge `i` running from point `i` to point `i + 1` (closing edge last). */
export function edgeMidpoints(polyPx) {
  if (!isPxOutline(polyPx)) return [];
  return polyPx.map((p, i) => {
    const q = polyPx[(i + 1) % polyPx.length];
    return [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
  });
}

/**
 * The indexes of the edges whose circle is shown: the edge is at least 24 CSS
 * px long on screen (48 with a coarse pointer), so the circle never sits on a
 * point. The index is the edge's, as in `edgeMidpoints`.
 */
export function shownEdgeIndexes(polyPx, pxPerScreenPx, coarse = false) {
  if (!isPxOutline(polyPx) || !Number.isFinite(pxPerScreenPx) || pxPerScreenPx <= 0) return [];
  const min = coarse === true ? EDGE_MIN_SCREEN_PX_COARSE : EDGE_MIN_SCREEN_PX;
  const out = [];
  polyPx.forEach((p, i) => {
    const q = polyPx[(i + 1) % polyPx.length];
    if (Math.hypot(q[0] - p[0], q[1] - p[1]) / pxPerScreenPx >= min) out.push(i);
  });
  return out;
}

/** The index of the nearest point within `radius`, or -1. */
function nearestWithin(points, at, radius) {
  let best = -1;
  let bestDist = radius;
  points.forEach((q, i) => {
    const d = Math.hypot(q[0] - at[0], q[1] - at[1]);
    if (d <= bestDist) {
      best = i;
      bestDist = d;
    }
  });
  return best;
}

/**
 * The hit order of a press on the SELECTED outline: (1) the NEAREST handle
 * within reach — a point → `{ kind: "vertex", index }` or an edge circle →
 * `{ kind: "edge", index }` (none at 16 points, none on a short edge), by
 * distance to its centre, a tie going to the point; (2) the inside →
 * `{ kind: "inside" }`; (3) anything else → `{ kind: "none" }`. Handles and
 * edge circles count only when `handlesVisible`; the inside always does, so
 * a small hold can still be moved.
 *
 * `opts.pointerType` ("touch" | "pen" | "mouse") picks the grab radii: touch
 * and pen reach 22 / 20 CSS px, a mouse 10 / 9. Without a pointer type the
 * radii follow `opts.coarse`. `opts.coarse` (the `(pointer: coarse)` query)
 * also sets the visibility threshold and the shortest edge that has a circle.
 * `opts.maxPoints` (default 16) — no edge circle at that many points (32 for
 * the wall outline).
 */
export function shapeHitAt(polyPx, point, pxPerScreenPx, opts = {}) {
  if (!isPxOutline(polyPx) || !isPxPoint(point)) return { kind: "none" };
  const coarse = opts && opts.coarse === true;
  if (handlesVisible(polyPx, pxPerScreenPx, coarse)) {
    const type = opts ? opts.pointerType : undefined;
    const fingerReach = type === "touch" || type === "pen" || (type !== "mouse" && coarse);
    const vertexReach = (fingerReach ? VERTEX_HIT_TOUCH_SCREEN_PX : VERTEX_HIT_SCREEN_PX) * pxPerScreenPx;
    const edgeReach = (fingerReach ? EDGE_HIT_TOUCH_SCREEN_PX : EDGE_HIT_SCREEN_PX) * pxPerScreenPx;
    const v = nearestWithin(polyPx, point, vertexReach);
    let best = v >= 0 ? { kind: "vertex", index: v, dist: Math.hypot(polyPx[v][0] - point[0], polyPx[v][1] - point[1]) } : null;
    const maxPoints = opts && Number.isInteger(opts.maxPoints) ? opts.maxPoints : POLY_MAX_POINTS;
    if (polyPx.length < maxPoints) {
      const mids = edgeMidpoints(polyPx);
      const shown = new Set(shownEdgeIndexes(polyPx, pxPerScreenPx, coarse));
      shown.forEach((i) => {
        const d = Math.hypot(mids[i][0] - point[0], mids[i][1] - point[1]);
        // Strictly nearer only: on a tie the point keeps the press.
        if (d <= edgeReach && (!best || d < best.dist)) best = { kind: "edge", index: i, dist: d };
      });
    }
    if (best) return { kind: best.kind, index: best.index };
  }
  return holdAtPoint([{ points: polyPx }], point) ? { kind: "inside" } : { kind: "none" };
}

/**
 * What a drag that starts at the press point does (the "Kiválasztás —
 * moving and point editing" table): only the Kiválasztás tool on an
 * editable selected outline takes it over — `vertex` (drag that point),
 * `insert` (a new point on that edge, dragged at once) or `move` (the whole
 * hold); everything else (`pan`) is the photo's own pan.
 */
export function decideDrag({ tool, editable, hit } = {}) {
  // The Fal kontúr tool edits the wall outline with the same gestures.
  if ((tool !== "select" && tool !== "outline") || editable !== true || !hit || typeof hit !== "object") return { type: "pan" };
  if (hit.kind === "vertex") return { type: "vertex", index: hit.index };
  if (hit.kind === "edge") return { type: "insert", index: hit.index };
  if (hit.kind === "inside") return { type: "move" };
  return { type: "pan" };
}

/** A px point kept inside the photo. */
function clampToPhoto(p, size) {
  return [Math.min(size.width, Math.max(0, p[0])), Math.min(size.height, Math.max(0, p[1]))];
}

/** A new point on edge `edgeIndex` (after point `edgeIndex`), clamped to the photo; null at `maxPoints` (16 for a hold, 32 for the wall outline) or on bad input. */
export function insertVertex(polyPx, edgeIndex, point, size, maxPoints = POLY_MAX_POINTS) {
  if (!isPxOutline(polyPx) || !isPxPoint(point) || !usableSize(size)) return null;
  if (polyPx.length >= maxPoints || !Number.isInteger(edgeIndex) || edgeIndex < 0 || edgeIndex >= polyPx.length) return null;
  const out = polyPx.map((p) => [p[0], p[1]]);
  out.splice(edgeIndex + 1, 0, clampToPhoto(point, size));
  return out;
}

/** Point `index` moved to `point`, clamped to the photo; null on bad input. */
export function moveVertex(polyPx, index, point, size) {
  if (!isPxOutline(polyPx) || !isPxPoint(point) || !usableSize(size)) return null;
  if (!Number.isInteger(index) || index < 0 || index >= polyPx.length) return null;
  return polyPx.map((p, i) => (i === index ? clampToPhoto(point, size) : [p[0], p[1]]));
}

/** Point `index` removed; null when only 3 points are left (the toast says why) or on bad input. */
export function deleteVertex(polyPx, index) {
  if (!isPxOutline(polyPx) || polyPx.length <= MIN_OUTLINE_POINTS) return null;
  if (!Number.isInteger(index) || index < 0 || index >= polyPx.length) return null;
  return polyPx.filter((_, i) => i !== index).map((p) => [p[0], p[1]]);
}

/**
 * The whole outline shifted by `(dx, dy)` px. The SHIFT is clamped so the
 * outline stays inside the photo with its shape intact (clamping each point
 * instead would squash a hold pushed against the edge). Null on bad input.
 */
export function translatePoly(polyPx, dx, dy, size) {
  if (!isPxOutline(polyPx) || !Number.isFinite(dx) || !Number.isFinite(dy) || !usableSize(size)) return null;
  const b = outlineBox(polyPx);
  const sx = Math.min(size.width - b.x1, Math.max(-b.x0, dx));
  const sy = Math.min(size.height - b.y1, Math.max(-b.y0, dy));
  return polyPx.map((p) => [p[0] + sx, p[1] + sy]);
}

/**
 * Can the outline of this item be moved / edited? Edit mode: a saved hold
 * that is not `missing`. Review mode: an accepted proposal or an added item
 * (an old kept hold is changed after the approval, in edit mode).
 */
export function isShapeEditable(item, mode) {
  if (!item || typeof item !== "object" || !Array.isArray(item.points)) return false;
  if (mode === "edit") return item.kind === "hold" && item.missing !== true;
  if (item.kind === "proposal") return item.status === "accepted";
  return item.kind === "added";
}

// ── The wall outline (21p "Fal kontúr — the wall outline") ──

/**
 * The wall outline as an editable px outline: the stored polygon, or — with
 * no outline yet — the 4 photo corners (nothing is stored until it changes).
 * `[]` for an unusable size.
 */
export function outlineEditPx(outline, width, height) {
  if (!(width > 0) || !(height > 0)) return [];
  if (isOutlinePoly(outline)) return pointsToPx(decodePoly(outline), width, height);
  return [[0, 0], [width, 0], [width, height], [0, height]];
}

/**
 * A px outline → the stored wall-outline string (normalized, clamped to the
 * photo), or null when it is not storable (fewer than 3 or more than 32
 * points, under 5 % of the photo, unusable size).
 */
export function pxToOutline(pxPoints, width, height) {
  if (!isPxOutline(pxPoints) || !(width > 0) || !(height > 0)) return null;
  const clip = (v) => Math.min(1, Math.max(0, v));
  const outline = encodePoly(pxPoints.map((p) => [clip(p[0] / width), clip(p[1] / height)]));
  return isOutlinePoly(outline) ? outline : null;
}

/**
 * The holds the approval would store, as `{ poly }` items — the input of
 * „Automatikus kontúr" in review mode: the accepted proposals (with their
 * current outline), the added items and the kept old holds.
 */
export function reviewOutlineHolds(state) {
  if (!state || typeof state !== "object") return [];
  const keep = asIdSet(state.keep);
  const out = [];
  for (const p of Array.isArray(state.proposals) ? state.proposals : []) if (p && p.accepted) out.push({ poly: p.poly });
  for (const a of Array.isArray(state.added) ? state.added : []) if (a) out.push({ poly: a.poly });
  for (const h of Array.isArray(state.oldHolds) ? state.oldHolds : []) if (h && keep.has(h.id) && h.missing !== true) out.push({ poly: h.poly });
  return out;
}

/**
 * The review items whose centre lies outside `outline` and that the approval
 * would store: accepted proposals (`idx`), added items (`tempId`), kept old
 * holds (`id`). The input of the reducer's `rejectOutside` and of its button's
 * count. Nothing without a storable outline.
 */
export function outsideTargets(state, outline) {
  const out = { proposals: [], added: [], old: [], count: 0 };
  if (!state || typeof state !== "object" || !isOutlinePoly(outline)) return out;
  const keep = asIdSet(state.keep);
  for (const p of Array.isArray(state.proposals) ? state.proposals : []) {
    if (p && p.accepted && holdOutsideOutline({ poly: p.poly }, outline)) out.proposals.push(p.idx);
  }
  for (const a of Array.isArray(state.added) ? state.added : []) {
    if (a && holdOutsideOutline({ poly: a.poly }, outline)) out.added.push(a.tempId);
  }
  for (const h of Array.isArray(state.oldHolds) ? state.oldHolds : []) {
    if (h && keep.has(h.id) && holdOutsideOutline({ poly: h.poly }, outline)) out.old.push(h.id);
  }
  out.count = out.proposals.length + out.added.length + out.old.length;
  return out;
}

// ── The local review state (21p "The resumable review and the local review state") ──

const isFiniteTransform = (t) => !!t && typeof t === "object" && [t.tx, t.ty, t.scale, t.rot].every(Number.isFinite);

/**
 * The manager's decisions of a review, as plain JSON for the browser storage:
 * every proposal's `accepted` / `feetOnly` (and its outline when corrected by
 * hand), the added items, `keep` (refresh only) and — after a manual
 * re-match of a photo swap — the transform it was made with. Not stored: the
 * selection, the undo stack, the view.
 */
export function reviewSnapshot(state) {
  if (!state || typeof state !== "object" || !Array.isArray(state.proposals)) return null;
  const snap = {
    proposals: state.proposals.map((p) => (p.edited === true
      ? { idx: p.idx, accepted: p.accepted === true, feetOnly: p.feetOnly === true, poly: p.poly }
      : { idx: p.idx, accepted: p.accepted === true, feetOnly: p.feetOnly === true })),
    added: (Array.isArray(state.added) ? state.added : []).map((a) => ({ poly: a.poly, src: a.src, feetOnly: a.feetOnly === true })),
  };
  if (state.mode === "refresh") snap.keep = [...asIdSet(state.keep)];
  if (state.outlineEdited === true) snap.outline = isOutlinePoly(state.outline) ? state.outline : null;
  if (state.photoSwap === true && state.manualMatches === true && isFiniteTransform(state.transform)) {
    snap.transform = { tx: state.transform.tx, ty: state.transform.ty, scale: state.transform.scale, rot: state.transform.rot };
  }
  return snap;
}

/**
 * A stored snapshot laid over the review state built from the run. Only what
 * still fits is taken: a proposal index the run has, a storable outline, an
 * added item of a valid source, a `keep` id among the old holds. A stored
 * transform re-matches the photo swap first (`realignReview`), so the
 * proposals' pairing is the one the decisions were made on. Malformed input
 * returns the state unchanged; the selection is always cleared.
 */
export function applyReviewSnapshot(state, snap, { versionHolds, proposals, fromSize, toSize, versionOutline = null } = {}) {
  if (!state || typeof state !== "object" || !snap || typeof snap !== "object" || !Array.isArray(snap.proposals)) return state;
  let next = state;
  if (next.photoSwap === true && isFiniteTransform(snap.transform)) {
    next = realignReview(next, { transform: snap.transform, versionHolds, proposals, fromSize, toSize, versionOutline });
  }
  const byIdx = new Map();
  for (const p of snap.proposals) {
    if (p && typeof p === "object" && Number.isInteger(p.idx)) byIdx.set(p.idx, p);
  }
  const items = next.proposals.map((p) => {
    const s = byIdx.get(p.idx);
    if (!s) return p;
    const out = { ...p, accepted: s.accepted !== false, feetOnly: s.feetOnly === true };
    const points = typeof s.poly === "string" && validateHoldPoly(s.poly) ? outlinePoints(s.poly) : null;
    return points ? { ...out, poly: s.poly, points, edited: true } : out;
  });
  let nextTempId = next.nextTempId;
  const added = [];
  for (const a of Array.isArray(snap.added) ? snap.added : []) {
    if (!a || typeof a !== "object" || !ADDED_SOURCES.has(a.src) || !validateHoldPoly(a.poly)) continue;
    const points = outlinePoints(a.poly);
    if (!points) continue;
    added.push({ tempId: nextTempId, points, poly: a.poly, src: a.src, feetOnly: a.feetOnly === true });
    nextTempId += 1;
  }
  let keep = next.keep;
  if (next.mode === "refresh" && Array.isArray(snap.keep)) {
    const oldIds = new Set(next.oldHolds.map((h) => h.id));
    keep = new Set(snap.keep.filter((id) => oldIds.has(id)));
  }
  // An edited wall outline (a string, or null = cleared) comes back as it was saved.
  let outlineFields = {};
  if ("outline" in snap && (snap.outline === null || isOutlinePoly(snap.outline))) outlineFields = { outline: snap.outline, outlineEdited: true };
  return { ...next, proposals: items, added: [...next.added, ...added], keep, nextTempId, selected: null, ...outlineFields };
}

/**
 * Did the manager change anything in the review compared to `initial` (the
 * state built from the run)? Selection does not count; a corrected outline,
 * an added item, a rejection, a feet-only flag, a `keep` change and a manual
 * re-match do.
 */
export function reviewChanged(state, initial) {
  if (!state || !initial) return false;
  if (state.added.length > 0) return true;
  const before = new Map(initial.proposals.map((p) => [p.idx, p]));
  for (const p of state.proposals) {
    const b = before.get(p.idx);
    if (!b || b.accepted !== p.accepted || b.feetOnly !== p.feetOnly || b.poly !== p.poly) return true;
  }
  if (state.keep.size !== initial.keep.size) return true;
  for (const id of state.keep) if (!initial.keep.has(id)) return true;
  // A manual re-match changes the payload (matches + transform) even when the pairing came out the same.
  if (state.manualMatches === true && initial.manualMatches !== true) return true;
  // The wall outline counts as a decision too.
  if ((state.outline ?? null) !== (initial.outline ?? null)) return true;
  return false;
}
